import makeStyles from '@mui/styles/makeStyles';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {user} from '../../../store/reducers/user';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import {useTranslation} from 'react-i18next';
import Formatter from '../../../util/formatter';
import moment from 'moment';
import Grid from '@mui/material/Grid';
import AgyleSelectField from '../../../components/ui/selector/agyleSelectField';
import Notes from '../../../util/notes';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import ObsNumericStepper from '../forms/observation/components/obsNumericStepper';
import _ from 'lodash';
import MiniFormDialog from '../../../components/modal/miniFormDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background + ` !important`,
    },
    groupRoot: {
        marginBottom: 10,
        padding: 5,
        borderRadius: 5,
    },
    paper: {
        padding: 10,
        backgroundColor: '#252525',
    },
    centerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    userLog: {
        fontSize: 11,
        fontStyle: 'italic',
        color: theme.palette.mode === 'light' ? '#4B4B4B' : '#818181',
    },
    comment: {
        fontSize: 15,
        padding: 5,
        backgroundColor: theme.palette.background.default,
        borderRadius: 5,
    },
    clinician: {
        marginBottom: 10,
    },
    row: {
        width: '100%',
        marginBottom: 10,
    },
    divider: {
        margin: '10px 0',
    },
    label: {
        color: theme.palette.inPatient.questionText,
        fontSize: 12,
        fontWeight: 600,
        marginLeft: 10,
        marginBottom: 5,
    },
}));

const OXYGEN_LEVELS = [
    {
        id: '1',
        name: '88-92%',
        lower: 88,
        upper: 92,
    }, {
        id: '2',
        name: '94-98%',
        lower: 94,
        upper: 98,
    }, {
        id: 'other',
        name: 'other',
    },
];

const OXYGEN_FORM_INIT = {
    hypercapnic: null,
    noHypercapnic: null,
    entryTargetSats: '',
    lower: 0,
    upper: 0,
    who: '',
    date: '',
};

function OxygenForm(props) {
    const classes = useStyles();
    const {t} = useTranslation();

    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    const [form, setForm] = useState(OXYGEN_FORM_INIT);

    useEffect(() => {
        setForm((formLast) => {
            return {
                ...formLast,
                who: userFullName,
                date: moment().toISOString(),
            };
        });
    }, [userObject]);

    useEffect(() => {
        if (props.formData) {
            let formClone = _.cloneDeep(props.formData);
            setForm({
                ...formClone,
                who: userFullName,
                date: moment().toISOString(),
            });
        }
    }, [props.formData]);

    useEffect(() => {
        if (props.changeFormData) {
            props.changeFormData(form);
        }
        if (props.formIsValid) {
            props.formIsValid(formIsValid());
        }
    }, [form]);

    const formIsValid = () => {
        return form && form.upper && form.upper && form.hypercapnic !== null && form.noHypercapnic !== null;
    };

    const handleFieldChange = (value, path) => {
        if (props.formIsChanged) props.formIsChanged(true);

        setForm((formLast) => {
            return {
                ...formLast,
                [path]: value,
            };
        });
    };

    const handleTargetFieldChange = (id, path) => {
        if (props.formIsChanged) props.formIsChanged(true);

        let value = _.find(OXYGEN_LEVELS, {id: id});
        setForm((formLast) => {
            return {
                ...formLast,
                [path]: id,
                lower: value?.lower || 0,
                upper: value?.upper || 0,
            };
        });
    };

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={10}>
                <div>
                    {t('This patient has')}
                    <span style={{color: '#E25B61', margin: '0px 5px'}}>
                        {t('NOT BEEN DIAGNOSED')}
                    </span>
                    {t('with Hypercapnic respiratory failure')}
                </div>
            </Grid>

            <Grid item xs={2}>
                <AgyleBlockSwitch
                    onChange={(e) => handleFieldChange(e, 'hypercapnic')}
                    value={form.hypercapnic}
                />
            </Grid>

            <Grid item xs={12} style={{borderBottom: '1px solid #626466'}}/>

            <Grid item xs={10}>
                <div>
                    {t('This Patient')}
                    <span style={{color: '#E25B61', margin: '0px 5px'}}>
                        {t('has confirmed Hypercapnic respiratory failure')}
                    </span>
                    {t('on blood gas analysis on either a prior or their current hospital admission')}
                </div>
            </Grid>

            <Grid item xs={2}>
                <AgyleBlockSwitch
                    onChange={(e) => handleFieldChange(e, 'noHypercapnic')}
                    value={form.noHypercapnic}
                />
            </Grid>

            <Grid item xs={form.entryTargetSats === 'other' ? 6 : 12}>
                <div className={classes.label}>
                    {t('Entry target sats')}
                </div>

                <AgyleSelectField
                    onChange={(e) => handleTargetFieldChange(e, 'entryTargetSats')}
                    options={OXYGEN_LEVELS}
                    withBackground
                    value={form.entryTargetSats}
                    padding="12px"
                    style={{marginBottom: 17}}
                    withBorder
                    size="large"
                    withNewBackground
                />
            </Grid>

            {form.entryTargetSats === 'other' &&
                <>
                    <Grid item xs={3}>
                        <ObsNumericStepper
                            value={form.lower}
                            noBackground
                            column
                            minValue={0}
                            maxValue={100}
                            label={t('lower (%)')}
                            precision={0}
                            step={1}
                            onChange={(e) => handleFieldChange(e, 'lower')}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <ObsNumericStepper
                            value={form.upper}
                            noBackground
                            column
                            minValue={0}
                            maxValue={100}
                            label={t('upper (%)')}
                            precision={0}
                            step={1}
                            onChange={(e) => handleFieldChange(e, 'upper')}
                        />
                    </Grid>
                </>
            }
        </Grid>
    );
}

function OxygenFormModal(props) {
    const {t} = useTranslation();

    const [formData, setFormData] = useState(null);

    const handleSubmit = () => {
        if (props.onChange) {
            props.onChange([{
                    name: 'oxygen',
                    value: formData,
                    noteToAdd: Notes.getOxygenNote(formData),
                }],
            );
        }
        if (props.onClose) props.onClose();
    };

    const removeOxygen = () => {
        if (props.onChange) {
            props.onChange([{
                    name: 'oxygen',
                    value: null,
                    noteToAdd: Notes.getOxygenNote(null),
                }],
            );
        }
        if (props.onClose) props.onClose();
    };

    return (
        <MiniFormDialog
            title={t(props._formTitle)}
            open={props.open}
            onClose={props.onClose}
            formOptions={props._formOptions}
            disableSubmit={props.disableSave}
            onSave={handleSubmit}
            buttons={[
                <AgyleButton
                    disabled={props.disableSave}
                    onClick={removeOxygen}
                >
                    {t('Delete')}
                </AgyleButton>,
            ]}
        >
            <OxygenForm
                formData={props.patient?.oxygen}
                changeFormData={setFormData}
            />
        </MiniFormDialog>
    );
}

export default OxygenFormModal;